/* eslint-disable @next/next/no-html-link-for-pages */
import { useToast } from "@alta/admin";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/nextjs";
import _ from "lodash";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { signIn, useSession } from "next-auth/react";
import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";

import { CountryNavbarComponent } from "../components/Auth/CountryDropdown/CountryDropdown";
import { AuthLoginComponent } from "../components/Auth/Login/Login";
import {
  LoginFormSchema,
  LoginFormSchemaType,
} from "../components/Auth/Login/LoginFormSchema";
import { AuthOtpComponent } from "../components/Auth/Otp/Otp";
import {
  OtpFormSchema,
  OtpFormSchemaType,
} from "../components/Auth/Otp/OtpFormSchema";
import AuthLayout from "../components/Layouts/Auth/Auth";
import { PrimeLink } from "../components/prime/link";
import { authenticateUserController } from "../lib/controllers/auth/authenticateUser";
import { authGetOtpController } from "../lib/controllers/auth/otp/get";
import getCampaign from "../lib/controllers/campaign/getCampaign";
import { renew2022CampaignData } from "../lib/data/campaign/renew2022";
import { featureFlag } from "../lib/featureFlags/featureFlags";
import { getIpCountry } from "../lib/helper/getIpCountry";
import { useSegmentAnalytics } from "../lib/hooks/useSegmentAnalytics";
import useSessionStorage from "../lib/hooks/useSessionStorage";
import { user } from "../lib/types/next-auth";
import { trackClickmagick } from "../lib/tracking/clickmagick";
import tenantConfig from "../tenant.config";
import { getLockoutStatus } from "@/services/users/getLockoutStatus";

export const getServerSideProps: GetServerSideProps = async ({
  query,
  req,
}) => {
  const { campaign, callbackUrl, callbackCountry } = query;
  const country: "SG" | "MY" =
    callbackCountry === "SG"
      ? "SG"
      : callbackCountry === "MY"
        ? "MY"
        : getIpCountry(req);
  //If theres country code, return them as props
  // If there's campaign code grab relevant campaign data and return them
  if (campaign) {
    const campaignData = getCampaign(campaign as string);
    if (campaignData) {
      return {
        props: {
          title: campaignData.login?.title,
          info: campaignData.login?.info,
          campaignCode: campaignData.data.code,
          callbackUrl: callbackUrl || null,
          countryCode: country || null,
        },
      };
    } else {
      return {
        redirect: {
          permanent: false,
          destination: "/signup",
        },
      };
    }
  }
  return {
    props: {
      callbackUrl: callbackUrl || null,
      countryCode: country || null,
    },
  };
};

/**************************************************
 *
 * View starts here.
 *
 **************************************************/

const IS_RECAPTCHA_ENABLED = featureFlag("recaptcha");

function LoginPage(props: {
  // For redirecting user back to previous page after login
  callbackUrl?: string;

  // For completing login
  email?: string;

  // For displaying campaign-related data
  campaignCode?: string;
  title?: string;
  info?: string;
  // For country-specific views
  countryCode?: string;
  sessionCountry: string;
}) {
  const [email, setEmail] = useState<string>("");
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isLoginSuccess, setIsLoginSuccess] = useState<boolean>(false);
  const [shouldShowOtpForm, setShouldShowOtpForm] = useState<boolean>(false);
  const [lockoutStatus, setLockoutStatus] = useState<{
    locked: boolean;
    remainingSeconds: number;
  }>({
    locked: false,
    remainingSeconds: 0,
  });
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const useLoginFormProps = useForm<LoginFormSchemaType>({
    resolver: zodResolver(LoginFormSchema),
  });
  const useOtpFormProps = useForm<OtpFormSchemaType>({
    resolver: zodResolver(OtpFormSchema),
  });
  const { info, callbackUrl } = props;
  const router = useRouter();
  const { segmentAnalytics } = useSegmentAnalytics();
  const session = useSession();
  const { setItem, getItem } = useSessionStorage();
  //to wait for country useEffect to load
  const [isLoading, setLoading] = useState<boolean>(true);
  const [currentCountry, setCurrentCountry] = useState<string>("");
  const [lockoutCountdown, setLockoutCountdown] = useState<number>(0);
  const { toast } = useToast();

  // Check lockout status when email changes (debounced)
  const checkLockoutStatus = async (emailValue: string) => {
    if (!emailValue || emailValue.length < 3) return;

    try {
      const result = await getLockoutStatus(emailValue);
      if (result && result.success) {
        setLockoutStatus(result.data);
        if (result.data.locked && result.data.remainingSeconds > 0) {
          setLockoutCountdown(result.data.remainingSeconds);
        }
      }
    } catch (error) {
      console.error("Error checking lockout status:", error);
    }
  };

  // Debounced email check - wait 1.5 seconds after user stops typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const watchedEmail = useLoginFormProps.watch("email");
      if (watchedEmail && watchedEmail !== email && watchedEmail.includes('@')) {
        setEmail(watchedEmail);
        checkLockoutStatus(watchedEmail);
      }
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [useLoginFormProps.watch("email")]);

  // Countdown timer for lockout
  useEffect(() => {
    if (lockoutCountdown > 0) {
      const timer = setInterval(() => {
        setLockoutCountdown((prev) => {
          if (prev <= 1) {
            // Refresh lockout status when countdown reaches 0
            const currentEmail = useLoginFormProps.getValues("email");
            if (currentEmail) {
              checkLockoutStatus(currentEmail);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [lockoutCountdown]);

  useEffect(() => {
    if (router.query.msg === "session_expired") {
      toast({
        title: "Session expired",
        description: "Please login again to access the platform.",
        variant: "default",
      });
    }
  }, [router.query]);

  useEffect(() => {
    const sessionCountry = getItem("selectedCountry");
    if (sessionCountry) {
      setCurrentCountry(sessionCountry);
    } else {
      setCurrentCountry(props?.countryCode === "MY" ? "MY" : "SG");
    }
    setLoading(false);
  }, [props.countryCode]);
  const setCountry = (selectedCountry: string) => {
    setCurrentCountry(selectedCountry);
    setShouldShowOtpForm(false);
    useLoginFormProps.reset();
    setItem("selectedCountry", selectedCountry);
  };

  async function identifyUserOnSegment(user: user) {
    await segmentAnalytics.identify(user.userId, {
      createdAt: new Date(),
      userId: user.userId,
      traderId: user.traderId,
      traderType: user.traderType,
      accreditationStatusCurrent: user.accreditationStatusCurrent,
      hasVerifiedAccreditation: user.hasVerifiedAccreditation,
      hasExpiredAccreditation: user.hasExpiredAccreditation,
      accreditationExpiresAtCurrent: user.accreditationExpiresAtCurrent,
      accreditationExpiringSoonCurrent: user.accreditationExpiringSoonCurrent,
      kycStatusCurrent: user.kycStatusCurrent,
      hasVerifiedKyc: user.hasVerifiedKyc,
      kycExpiresAtCurrent: user.kycExpiresAtCurrent,
      kycExpiringSoonCurrent: user.kycExpiringSoonCurrent,
    });

    await segmentAnalytics.track("Investor Login");
  }

  function identifyUseronSentry(user: user) {
    !_.isNil(user.userEmail) && Sentry.setUser({ email: user.userEmail });
    Sentry.setContext("User", user as unknown as { [key: string]: unknown });
  }

  useEffect(() => {
    if (
      session &&
      session.data &&
      session.data.user &&
      session.status === "authenticated"
    ) {
      identifyUserOnSegment(session.data.user);
      identifyUseronSentry(session.data.user);
    }
  }, [session]);

  const onSubmitLogin = async (data: any) => {
    setIsFetching(true);

    try {
      let token = "default";

      // Use reCAPTCHA if enabled (preserving original behavior)
      if (IS_RECAPTCHA_ENABLED) {
        if (!recaptchaRef.current) throw "reCaptcha null";
        token = (await recaptchaRef.current.executeAsync()) ?? "";
        if (!token) throw "token null";
      }

      // Authenticate email & password (with CAPTCHA token if reCAPTCHA is enabled)
      const [authRes, authError] = await authenticateUserController(
        data.email,
        data.password,
        IS_RECAPTCHA_ENABLED ? token : undefined
      );

      // Handle authentication errors
      if (authError) {
        if (authError.locked) {
          // Account is locked - refresh lockout status and show appropriate message
          await checkLockoutStatus(data.email);

          toast({
            title: "Account Temporarily Locked",
            description: `Your account is locked. Please try again in ${Math.ceil(authError.remainingSeconds / 60)} minutes.`,
            variant: "destructive",
            duration: 3000,
          });
        } else if (authError.message?.includes("Additional verification")) {
          // CAPTCHA required or invalid
          useLoginFormProps.setError("root", {
            type: "custom",
            message: "Additional verification required. Please try again.",
          });
          recaptchaRef.current?.reset();
        } else {
          // Generic authentication error (invalid credentials)
          // Check lockout status after failed attempt to see if CAPTCHA is now required
          await checkLockoutStatus(data.email);

          useLoginFormProps.setError(
            "email",
            {
              type: "custom",
              message: "Invalid username or password, please try again.",
            },
            { shouldFocus: true }
          );
          useLoginFormProps.setError(
            "password",
            {
              type: "custom",
              message: "Invalid username or password, please try again.",
            },
            { shouldFocus: true }
          );
        }

        setIsFetching(false);
        recaptchaRef.current?.reset();
        return;
      }

      // Request OTP
      const [_, error] = await authGetOtpController(
        data.email,
        "login",
        token,
        currentCountry
      );
      // If email is not found, still show OTP form
      if (error && error.message !== "Not Found") {
        useLoginFormProps.setError(
          "email",
          { type: "custom", message: error.message + "" },
          { shouldFocus: true }
        );
        setIsFetching(false);
        return;
      }

      // Save info on localStorage to make the otp expiration time persistent
      // when the page is refreshed or the user tries to log in again in another tab
      const otpCountdownData = {
        email: data.email,
        otpSentAt: new Date().toJSON(),
        action: "login",
        currentCountry,
      };
      localStorage.setItem(
        "otpCountdownData",
        JSON.stringify(otpCountdownData)
      );

      // Show OTP form
      setEmail(data.email);
      setShouldShowOtpForm(true);
    } catch (error) {
      useLoginFormProps.setError(
        "email",
        { type: "custom", message: error + "" },
        { shouldFocus: true }
      );
      recaptchaRef.current?.reset();
    } finally {
      setIsFetching(false);
    }
  };

  const onSubmitOtp = async (data: any) => {
    setIsFetching(true);
    const next = ["/verify", "/kyc/verify"].includes(
      router.query?.next as string
    )
      ? (router.query.next as string)
      : null;

    // Process login
    const result = await signIn("login", {
      email,
      otp: data.code,
      country: currentCountry,
      redirect: false,
      callbackUrl: decodeURIComponent(
        next || callbackUrl || "/terms-and-conditions"
      ),
    });

    // If login process fail, show error to user
    if (result && result.ok === false) {
      const errorMessage = result.error ?? "An error occurred"; // Use a default value if result.error is null
      useOtpFormProps.setError(
        "code",
        { type: "custom", message: errorMessage },
        { shouldFocus: true }
      );
      setIsFetching(false);
      return;
    }

    // Redirect on successful signup
    setIsLoginSuccess(true);
    trackClickmagick({
      cmc_goal: "a",
      cmc_ref: "Platform_Login_Submit",
      cmc_email: email,
    });
    if (result && result.url) {
      localStorage.removeItem("otpCountdownData");
      sessionStorage.removeItem("selectedCountry");
      router.push(result.url!);
    }
  };

  // If diff between current time & localStorage time is < 5 mins show otp form
  useEffect(() => {
    const storedDataString = localStorage.getItem("otpCountdownData");
    if (storedDataString) {
      const storedData = JSON.parse(storedDataString);
      const email = storedData.email;
      const action = storedData.action;
      const otpSentAt = new Date(storedData.otpSentAt);
      const now = new Date();
      const timeDifference = Number(now) - Number(otpSentAt);
      const secondsDifference = Math.floor(timeDifference / 1000);

      if (action !== "login") {
        localStorage.removeItem("otpCountdownData");
        return;
      }

      if (secondsDifference > 300) {
        localStorage.removeItem("otpCountdownData");
      } else {
        setEmail(email);
        setShouldShowOtpForm(true);
      }
    }
  }, []);

  const renderFooter = () => {
    switch (tenantConfig.entity_short_name.toLowerCase()) {
      case "alta":
        return (
          <>
            If you have any issues with signing in, please reach out via our
            Helpdesk by clicking on the icon below or contact us at{" "}
            <PrimeLink href={"mailto:" + tenantConfig.email.generic}>
              {tenantConfig.email.generic}
            </PrimeLink>
            .
          </>
        );
      default:
        return (
          <>
            If you have any issues with signing in, contact us at{" "}
            <PrimeLink href={"mailto:" + tenantConfig.email.generic}>
              {tenantConfig.email.generic}
            </PrimeLink>
            .
          </>
        );
    }
  };

  const renderPasswordResetInfo = () => {
    switch (tenantConfig.entity_short_name.toLowerCase()) {
      case "alta":
        return (
          <section
            id="login-section-info-set-password"
            className="flex z-90 p-4 mt-6 mb-4 rounded-md border-solid	border border-orange-200 bg-orange-100"
          >
            <div>
              <ExclamationCircleIcon className="text-amber-700 w-5" />
            </div>

            <div className="text-amber-700 text-sm ml-2">
              Passwords are now required in addition to existing authentication
              methods. If you&apos;re an existing{" "}
              {tenantConfig.entity_short_name} user, please{" "}
              <PrimeLink href="/reset-password">click here</PrimeLink> to set
              your password.
            </div>
          </section>
        );
      default:
        return null;
    }
  };

  if (isLoginSuccess) {
    return (
      <>
        <Head>
          <title>Signing In - {tenantConfig.entity_short_name}</title>
        </Head>
        <AuthLayout componentId="login-auth" country={currentCountry}>
          <h1 className="font-light tracking-wide">
            Signing you into {tenantConfig.entity_short_name}
          </h1>
        </AuthLayout>
      </>
    );
  }

  const renderPlatformName = () => {
    switch (tenantConfig.entity_short_name.toLowerCase()) {
      case "dicree":
        return "ARCADE";
      default:
        return "Alta";
    }
  };

  return (
    <>
      <Head>
        <title>
          {props.title ? props.title : `Sign in - ${renderPlatformName()}`}
        </title>
      </Head>
      {featureFlag("ecf") && !isLoading && (
        <CountryNavbarComponent
          componentId="login-navbar"
          countryCode={currentCountry}
          setCountry={setCountry}
        />
      )}
      <AuthLayout componentId="login-auth" country={currentCountry}>
        {shouldShowOtpForm ? (
          <>
            <h1 className="font-light tracking-wide">
              Two-factor authentication
            </h1>
            <div id="login-verify-otp-box">
              <h2>Check your email</h2>
              <p>
                <span>Please enter OTP sent to:</span>
                <br />
                <span
                  id="login-auth-otp-text-email"
                  className="font-bold text-black"
                >
                  {email}
                </span>
              </p>
              <AuthOtpComponent
                useFormProps={useOtpFormProps}
                onSubmit={onSubmitOtp}
                isFetching={isFetching}
                componentId="login-auth-otp-form"
              />
              <p className="pt-2 text-xs leading-normal">
                You will receive an email if an account exists.
                <br />
                <span className="block mt-1"></span>
                {renderFooter()}
              </p>
            </div>
          </>
        ) : (
          <>
            <h1 className="font-light tracking-wide">
              Sign in to {renderPlatformName()}
            </h1>
            {/* 2FA info component */}
            {renderPasswordResetInfo()}
            <div>
              <h2>Welcome back</h2>
              <p>Sign in with your {tenantConfig.entity_short_name} account</p>

              {/* Account lockout status */}
              {lockoutStatus.locked && (
                <div className="flex z-90 p-4 mt-6 mb-4 rounded-md border-solid border border-red-200 bg-red-100">
                  <div>
                    <ExclamationCircleIcon className="text-red-700 w-5" />
                  </div>
                  <div className="text-red-700 text-sm ml-2">
                    Your account is temporarily locked.
                    {lockoutCountdown > 0 && (
                      <span className="font-semibold">
                        {" "}
                        Please try again in {Math.floor(lockoutCountdown / 60)}:
                        {String(lockoutCountdown % 60).padStart(2, "0")}.
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* reCAPTCHA - preserve original behavior */}
              {IS_RECAPTCHA_ENABLED && (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="invisible"
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""}
                  badge="bottomleft"
                />
              )}
              <AuthLoginComponent
                componentId="login-form"
                onSubmit={onSubmitLogin}
                useFormProps={useLoginFormProps}
                isFetching={isFetching}
                isLocked={lockoutStatus.locked}
                info={info}
              />
              <div
                id="login-text-signup"
                className="mx-auto text-sm !mt-4 w-fit"
              >
                Don&apos;t have an account?{" "}
                <PrimeLink
                  id="login-text-signup-link-create-account"
                  href={
                    props.campaignCode === renew2022CampaignData.data.code
                      ? "/renew2022"
                      : "/signup"
                  }
                >
                  Sign up
                </PrimeLink>
              </div>
            </div>
          </>
        )}
      </AuthLayout>
    </>
  );
}

export default LoginPage;
